export enum Role {
  User = "user",
  Assistant = "assistant",
  System = "system",
}

export enum Action {
  ChangeMode = "changemode",
  ResetMode = "resetmode",
  Transcription = "transcription",
  Message = "message",
  Response = "response",
  Setup = "setupmode",
  Analysis = "analysismode",
  End = "endmode",
  Thinking = "thinking",
}

export type VoiceChatMessage = {
  role: Role;
  action: Action;
  mode: string;
  modeStr: string;
  // handle better these optionals, create separate type better
  session?: string;
  clientTranscription?: string;
  message?: string;
  timestamp?: number;
  token?: string;
  audioSrc?: string;
  userLanguage?: string;
  userLanguageCode?: string;
  language?: string;
  languageCode?: string;
  hasFeedback?: boolean;
  feedback?: Feedback;
  recordingDuration?: number;
  speakingDuration?: number;
  proficiencyLevel?: string;
  audioSampleRate?: number;
};

export type Metrics = {
  coherence: number;
  cohesion: number;
  contundence: number;
  lexicalResource: number;
  pronunciation: number;
  fluency: number;
};

export type InteractionSummary = {
  date: string;
  mode: string;
  role: string;
  message: string;
  duration: number;
} & Metrics;

export type Feedback = {
  metrics: Metrics;
  analysis: string;
};

// Function to build websocket header
// This allows to send extra bytes of information when sending audio via websocket
export const buildWebsocketHeader = (message: VoiceChatMessage): string => {
  message.timestamp = Date.now();
  const LENGTH_HEADER_SIZE = 6;
  const msg: string = JSON.stringify(message);
  const lengthStr = msg.length.toString().padStart(LENGTH_HEADER_SIZE, "0");
  return lengthStr + msg;
};

// Builds a 44-byte RIFF/WAV header for raw PCM audio so it can be played by <audio> elements
const buildWavHeader = (
  dataLength: number,
  sampleRate: number,
  channels: number,
  bitsPerSample: number
): Uint8Array => {
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const byteRate = (sampleRate * channels * bitsPerSample) / 8;
  const blockAlign = (channels * bitsPerSample) / 8;

  // RIFF chunk
  view.setUint8(0, 0x52); view.setUint8(1, 0x49); view.setUint8(2, 0x46); view.setUint8(3, 0x46); // "RIFF"
  view.setUint32(4, 36 + dataLength, true); // file size - 8
  view.setUint8(8, 0x57); view.setUint8(9, 0x41); view.setUint8(10, 0x56); view.setUint8(11, 0x45); // "WAVE"
  // fmt chunk
  view.setUint8(12, 0x66); view.setUint8(13, 0x6d); view.setUint8(14, 0x74); view.setUint8(15, 0x20); // "fmt "
  view.setUint32(16, 16, true);           // fmt chunk size
  view.setUint16(20, 1, true);            // PCM format
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  // data chunk
  view.setUint8(36, 0x64); view.setUint8(37, 0x61); view.setUint8(38, 0x74); view.setUint8(39, 0x61); // "data"
  view.setUint32(40, dataLength, true);

  return new Uint8Array(header);
};

// Converts a base64-encoded raw PCM16 string into a WAV blob URL playable by <audio>
export const pcmBase64ToWavUrl = (base64: string, sampleRate = 24000): string => {
  const binaryStr = atob(base64);
  const pcmBytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    pcmBytes[i] = binaryStr.charCodeAt(i);
  }
  const wavHeader = buildWavHeader(pcmBytes.byteLength, sampleRate, 1, 16);
  const wavBytes = new Uint8Array(wavHeader.byteLength + pcmBytes.byteLength);
  wavBytes.set(wavHeader, 0);
  wavBytes.set(pcmBytes, wavHeader.byteLength);
  return URL.createObjectURL(new Blob([wavBytes], { type: "audio/wav" }));
};

// Function to parse a voicechat message bytes into VoiceChatMessage
export const parseVoiceChatMessage = (bytes: Uint8Array): VoiceChatMessage => {
  const LENGTH_HEADER_SIZE = 6;
  const decoder = new TextDecoder();

  const lengthStr = decoder.decode(bytes.slice(0, LENGTH_HEADER_SIZE));
  const lenght = parseInt(lengthStr, 10);
  const msg = decoder.decode(
    bytes.slice(LENGTH_HEADER_SIZE, LENGTH_HEADER_SIZE + lenght)
  );

  const parsedMessage = JSON.parse(msg) as VoiceChatMessage;

  const audio = bytes.slice(LENGTH_HEADER_SIZE + lenght);

  let audioSrc: string | undefined;
  if (audio.byteLength > 0) {
    const sampleRate = parsedMessage.audioSampleRate ?? 24000;
    const wavHeader = buildWavHeader(audio.byteLength, sampleRate, 1, 16);
    const wavBytes = new Uint8Array(wavHeader.byteLength + audio.byteLength);
    wavBytes.set(wavHeader, 0);
    wavBytes.set(audio, wavHeader.byteLength);
    audioSrc = URL.createObjectURL(new Blob([wavBytes], { type: "audio/wav" }));
  }

  return {
    ...parsedMessage,
    audioSrc,
  };
};
